/**
 * Derives one payapp's Lien Waiver detail row (contractor / through date / application # / amount)
 * from that payapp's own draw and budget summary.
 *
 * Extracted from LienWaiversSection so the section, the export and the tests all compute the row the
 * same way — each payapp answers Cumulative? and Thru Curr. Amt? independently, so this has to run
 * per payapp rather than once for the report.
 */

import { formatCurrency, formatISODate } from '../../../../common/utils/common.util';
import { orEmpty } from '../../../../common/utils/textFormat.util';

export interface LienWaiverDrawInputs {
  name?: string;
  applicationNumber?: string | number;
  periodEnding?: string;
  periodBeginning?: string;
  previousFundings?: Array<{ applicationNumber?: string | number; amountFunded?: number | null }>;
}

export interface LienWaiverBudgetInputs {
  lessPreviousFundings?: number | null;
  requestedAmount?: number | null;
}

export interface LienWaiverTableRow {
  thruDate: string;
  applNumber: string;
  amount: string;
}

/**
 * The amount depends on the Cumulative? × Thru Curr. Amt? matrix:
 * - cumulative + through current  → all previous fundings plus this application's request
 * - cumulative only               → all previous fundings
 * - through current only          → this application's request
 * - neither                       → the most recent single previous funding
 */
export const getLienWaiverTableRow = (
  isCumulative: boolean,
  throughCurrentPeriod: boolean,
  draw?: LienWaiverDrawInputs,
  budget?: LienWaiverBudgetInputs,
): LienWaiverTableRow => {
  const periodEnding = draw?.periodEnding ? formatISODate(draw.periodEnding) : '[Period Ending]';
  const periodBeginning = draw?.periodBeginning ? formatISODate(draw.periodBeginning) : '[Period Beginning]';
  const currentDrawNumber = draw?.applicationNumber || '[This Draw]';

  const previousFundings = draw?.previousFundings || [];
  const lastPreviousFunding = previousFundings.length > 0 ? previousFundings[previousFundings.length - 1] : null;
  const previousDrawNumber = lastPreviousFunding?.applicationNumber || '[Previous Draw]';

  let amount = '0';
  if (isCumulative && throughCurrentPeriod) {
    const prev = budget?.lessPreviousFundings ?? 0;
    const curr = budget?.requestedAmount ?? 0;
    amount = formatCurrency((prev + curr).toString(), false);
  } else if (isCumulative && !throughCurrentPeriod) {
    if (budget?.lessPreviousFundings != null) {
      amount = formatCurrency(budget.lessPreviousFundings.toString(), false);
    }
  } else if (!isCumulative && throughCurrentPeriod) {
    if (budget?.requestedAmount != null) {
      amount = formatCurrency(budget.requestedAmount.toString(), false);
    }
  } else {
    const lastAmountFunded = lastPreviousFunding?.amountFunded;
    if (lastAmountFunded != null) {
      amount = formatCurrency(lastAmountFunded.toString(), false);
    }
  }

  return {
    thruDate: throughCurrentPeriod ? periodEnding : periodBeginning,
    applNumber: `Application #${throughCurrentPeriod ? currentDrawNumber : previousDrawNumber}`,
    amount: amount ? `$${amount}` : '$0',
  };
};

export const buildLienWaiverContractorLabel = (contractorName: string | undefined): string => orEmpty(contractorName);
